import clsx from 'clsx';
import React from 'react';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

type AvatarProps = {
  alt?: string;
  /** Escape hatch for dynamic tenant colors. Prefer the CSS custom property --avatar-border-color instead. */
  borderColor?: string;
  className?: string;
  image?: string;
  onClick?: () => void;
  size?: AvatarSize;
};

function buildInlineBorderStyle(borderColor: string | undefined): React.CSSProperties | undefined {
  if (!borderColor) return undefined;
  return { '--avatar-border-color': borderColor } as React.CSSProperties;
}

function Avatar({
  alt = '',
  borderColor,
  className,
  image,
  onClick,
  size = 'md',
}: Readonly<AvatarProps>): JSX.Element {
  const style = buildInlineBorderStyle(borderColor);
  const isInteractive = Boolean(onClick);

  const classNames = clsx(
    'avatar',
    `avatar--size-${size}`,
    { 'avatar--with-border': Boolean(borderColor), 'avatar--interactive': isInteractive },
    className,
  );

  const placeholder = alt ? (
    <>
      <div aria-hidden className="avatar__placeholder" />
      <span className="sr-only">{alt}</span>
    </>
  ) : (
    <div aria-hidden className="avatar__placeholder" />
  );

  const content = image ? <img alt={alt} className="avatar__image" src={image} /> : placeholder;

  if (isInteractive) {
    return (
      <button className={classNames} onClick={onClick} style={style} type="button">
        {content}
      </button>
    );
  }

  return (
    <div className={classNames} style={style}>
      {content}
    </div>
  );
}

export default Avatar;
export type { AvatarProps, AvatarSize };
